import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useMSRNudgeRecommendations } from '@atlassian/cross-join-nudges/services/utils';
import { useSubProduct } from '@jira/platform__services-subproduct/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { useJpdCrossJoinNudgeEligibility } from './eligibility/useJpdCrossJoinNudgeEligibility.tsx';
import { useConfluenceCrossJoinNudgeEligibility } from './eligibility/useConfluenceCrossJoinNudgeEligibility.tsx';
import { useJpdCrossJoinNudge } from './nudges/jpd-nudge.tsx';
import { useLoomCrossJoinNudge } from './nudges/loom-nudge.tsx';
import { useConfluenceCrossJoinNudge } from './nudges/confluence-nudge.tsx';
import { useLoomCrossJoinNudgeEligibility } from './eligibility/loom-eligibility.tsx';

/**
 * CrossJoinNudges conditionally renders various prompts based on user and site context.
 * Rendering occurs in a side-effectful way as targeting and eligibility checks are made synchronously at runtime.
 */
export const CrossJoinNudges = (): React.JSX.Element => {
	return (
		<JSErrorBoundary id="JiraCrossJoinNudges" teamName="groot" packageName="jiraCrossJoinNudges">
			<CrossJoinNudgesWithoutErrorBoundary />
		</JSErrorBoundary>
	);
};

const useCrossJoinNudges = (): void => {
	const cloudId = useCloudId();
	const subProduct = useSubProduct();
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product: 'jira',
		sessionId: uuid(),
		cloudId,
		subProduct: subProduct || '',
	});

	const getJpdCrossJoinNudgeEligibility = useJpdCrossJoinNudgeEligibility({
		getMSRNudgeRecommendations,
	});
	const getLoomCrossJoinNudgeEligibility = useLoomCrossJoinNudgeEligibility();
	const getConfluenceCrossJoinNudgeEligibility = useConfluenceCrossJoinNudgeEligibility({
		getMSRNudgeRecommendations,
	});
	const showJpdNudge = useJpdCrossJoinNudge();
	const showLoomNudge = useLoomCrossJoinNudge();
	const showConfluenceNudge = useConfluenceCrossJoinNudge();

	useEffect(() => {
		async function executeNudges() {
			const loomEligibilityResult = await getLoomCrossJoinNudgeEligibility();
			if (loomEligibilityResult.isEligible) {
				showLoomNudge();
				return;
			}

			const jpdEligibilityResult = await getJpdCrossJoinNudgeEligibility();
			if (jpdEligibilityResult.isEligible) {
				showJpdNudge(jpdEligibilityResult.data);
				return;
			}

			const confluenceEligibilityResult = await getConfluenceCrossJoinNudgeEligibility();
			if (confluenceEligibilityResult.isEligible) {
				showConfluenceNudge(confluenceEligibilityResult.data);
			}
		}

		executeNudges();
	}, [
		getConfluenceCrossJoinNudgeEligibility,
		getJpdCrossJoinNudgeEligibility,
		getLoomCrossJoinNudgeEligibility,
		showConfluenceNudge,
		showJpdNudge,
		showLoomNudge,
	]);
};

const CrossJoinNudgesWithoutErrorBoundary = () => {
	useCrossJoinNudges();

	return null;
};

export default CrossJoinNudges;
